// Top-level routing: public sign-in pages, then the gate (signed in → account set-up finished → area access),
// then the area's module pages inside the shell.
import { Suspense, lazy, useEffect } from 'react'
import { isArea, landingPath, type Area, type Me } from '@briary/shared'
import { can, useAuth } from './lib/auth'
import { matchPath, Redirect, useRouter } from './lib/router'
import { canOpenPortalPath } from './lib/portal-modules'
import { canOpenStaffPath } from './lib/staff-modules'
import { canOpenSensitiveMoneyPath } from './lib/sensitive-money-access'
import { PORTAL_EXTRA_ROUTES } from './modules/portal/extra-modules'
import { ARCHIVE_ROUTES } from './modules/legacy-archive/entry'
import { routesFor } from './modules'
import { Shell } from './shell/Shell'
import { Empty, Loading } from './ui'
import Login from './pages/auth/Login'
import Forgot from './pages/auth/Forgot'
import Reset from './pages/auth/Reset'
import Choose from './pages/auth/Choose'
import Setup from './pages/auth/Setup'
import Account from './pages/account/Account'
import { AuthLayout } from './pages/auth/AuthLayout'

// Public quote approval and invoice view (token links from emails; no login).
const PublicQuotePage = lazy(() => import('./modules/sales/PublicQuote').then((m) => ({ default: m.PublicQuote })))
const PublicInvoicePage = lazy(() => import('./modules/sales/PublicQuote').then((m) => ({ default: m.PublicInvoice })))
const PublicBrelPage = lazy(() => import('./modules/epc/PublicBrel').then((m) => ({ default: m.PublicBrel })))
// Briary Site Manager: a full-window app without the hub shell.
const SitePlanAppPage = lazy(() => import('./modules/siteplans/SitePlanApp'))
const QuotePackPage = lazy(() => import('./modules/sales/pack/QuotePack'))
// Kaizen SAP: the desktop program's window, full screen without the hub shell.
const SapAppPage = lazy(() => import('./modules/sap/app/SapApp'))
const SapStartPage = lazy(() => import('./modules/sap/app/SapStart'))
const PsiAppPage = lazy(() => import('./modules/psi/app/PsiApp'))
const PsiStartPage = lazy(() => import('./modules/psi/app/PsiStart'))
const WindowReportPage = lazy(() => import('./modules/sap/windowReport/WindowReport'))
const Tm59ReportPage = lazy(() => import('./modules/models/tm59/Tm59Report'))
const HemReportPage = lazy(() => import('./modules/hem/HemReport'))
const PortalHemReportPage = lazy(() => import('./modules/hem/portal/PortalReport'))
const PartGReportPage = lazy(() => import('./modules/partg/PartGReport'))
const PvFitReportPage = lazy(() => import('./modules/hem/pvfit/PvFitReport'))
const DwellingPackPage = lazy(() => import('./modules/hem/pack/DwellingPack'))
// Development only: the TM59 report of the server's results fixture, without the API (dropped from production builds).
const Tm59FixturePage = import.meta.env.DEV ? lazy(() => import('./modules/models/tm59/Tm59Fixture')) : null

const needsSetup = (me: Me) => me.mustChangePassword || (me.mfa.required && !me.mfa.totp)

const LAST_AREA = 'briary.lastArea'
const rememberArea = (a: Area) => {
  try {
    sessionStorage.setItem(LAST_AREA, a)
  } catch {
    /* storage can be unavailable (private mode); harmless */
  }
}
const lastArea = (me: Me): Area => {
  try {
    const v = sessionStorage.getItem(LAST_AREA)
    if (isArea(v) && me.areas.includes(v)) return v
  } catch {
    /* ignore */
  }
  return me.preferredArea && me.areas.includes(me.preferredArea) ? me.preferredArea : (me.areas[0] ?? 'portal')
}

export function App() {
  const { me, loading } = useAuth()
  const { loc } = useRouter()
  const path = loc.path

  if (Tm59FixturePage && path === '/dev/tm59') return <Suspense fallback={<Loading />}><Tm59FixturePage /></Suspense>
  if (loading) return <Loading label="Opening Briary Hub…" />
  // Directory bookmarks also work when a static/dev server serves index.html directly.
  if(path==='/manager')return <Redirect to="/portal"/>
  if(path==='/subcontractor')return <Redirect to="/subcon"/>

  // Public pages
  if (path === '/login') return me && !needsSetup(me) ? <Redirect to={landingPath(me)} /> : <Login />
  if (path === '/forgot') return <Forgot />
  if (path === '/reset') return <Reset />
  const brel = /^\/b\/([^/]+)$/.exec(path)
  if (brel) return <Suspense fallback={<Loading />}><PublicBrelPage token={decodeURIComponent(brel[1])} /></Suspense>
  const pub = /^\/(q|i)\/([^/]+)$/.exec(path)
  if (pub) return <Suspense fallback={<Loading />}>{pub[1] === 'q' ? <PublicQuotePage token={decodeURIComponent(pub[2])} /> : <PublicInvoicePage token={decodeURIComponent(pub[2])} />}</Suspense>

  if (!me) {
    const back = path !== '/' ? `?next=${encodeURIComponent(path + (loc.search.size ? `?${loc.search}` : ''))}` : ''
    return <Redirect to={`/login${back}`} />
  }
  if (path === '/setup') return <Setup />
  if (needsSetup(me)) return <Redirect to="/setup" />

  if (path === '/') return <Redirect to={landingPath(me)} />
  if (path === '/choose') return me.areas.length > 1 ? <Choose /> : <Redirect to={landingPath(me)} />
  if (path === '/no-access') return <NoAccess />

  if (path === '/account') {
    return (
      <Shell area={lastArea(me)}>
        <Account />
      </Shell>
    )
  }

  const area = path.split('/')[1]
  if (!isArea(area)) return <NotFoundPage />
  if (!me.areas.includes(area)) return <Redirect to={landingPath(me)} />
  if (area === 'admin' && !canOpenSensitiveMoneyPath(me, path)) return <Shell area="admin"><div className="page"><Empty title="No access" icon="lock">Only a superadmin can open this page.</Empty></div></Shell>
  if (area === 'portal' && !canOpenPortalPath(me, path)) return <Shell area="portal"><div className="page"><Empty title="Module not enabled" icon="lock">Briary can enable this module for your account.</Empty></div></Shell>
  if (area === 'admin' && !canOpenStaffPath(me, path)) return <Shell area="admin"><div className="page"><Empty title="Module not enabled" icon="lock">Your administrator can enable this staff module for your account.</Empty></div></Shell>
  const sitePlan = area === 'admin' ? /^\/admin\/siteplans\/(\d+)$/.exec(path) : null
  if (sitePlan && can(me, 'siteplans.view')) return <Suspense fallback={<Loading label="Opening the site plan…" />}><SitePlanAppPage id={Number(sitePlan[1])} /></Suspense>
  // An enquiry's site report & quote pack (sales/pack): full screen, printable A4 sheets.
  const quotePack = area === 'admin' ? /^\/admin\/enquiries\/(\d+)\/pack\/?$/.exec(path) : null
  if (quotePack && can(me, 'enquiries.view'))
    return <Suspense fallback={<Loading label="Analysing the site…" />}><QuotePackPage enquiryId={Number(quotePack[1])} /></Suspense>
  // The window U-value report (sap/windowReport): full screen like the SAP app, printable A4 sheets.
  const windowReport = area === 'admin' ? /^\/admin\/sap\/window-report\/(\d+)\/?$/.exec(path) : null
  if (windowReport && can(me, 'sap.view'))
    return <Suspense fallback={<Loading label="Opening the report…" />}><WindowReportPage siteId={Number(windowReport[1])} /></Suspense>
  // A TM59 overheating run's report (models/tm59): full screen, printable A4 sheets.
  const tm59Report = area === 'admin' ? /^\/admin\/models\/tm59\/(\d+)\/?$/.exec(path) : null
  if (tm59Report && (can(me, 'models.view') || can(me, 'models.manage') || can(me, 'sap.view')))
    return <Suspense fallback={<Loading label="Opening the report…" />}><Tm59ReportPage jobId={Number(tm59Report[1])} /></Suspense>
  // A HEM run's report (hem): full screen, printable A4 sheets.
  const hemReport = area === 'admin' ? /^\/admin\/hem\/runs\/(\d+)\/report\/?$/.exec(path) : null
  if (hemReport && can(me, 'sap.view'))
    return <Suspense fallback={<Loading label="Opening the report…" />}><HemReportPage runId={Number(hemReport[1])} /></Suspense>
  // A HEM run's report for a client manager (hem/portal): the same sheets, read through the portal's access checks.
  const portalHemReport = area === 'portal' ? /^\/portal\/hem\/runs\/(\d+)\/report\/?$/.exec(path) : null
  if (portalHemReport)
    return <Suspense fallback={<Loading label="Opening the report…" />}><PortalHemReportPage runId={Number(portalHemReport[1])} /></Suspense>
  // A SAP dwelling's Part G water efficiency report (partg): full screen, printable A4 sheets.
  const partgReport = area === 'admin' ? /^\/admin\/partg\/dwellings\/(\d+)\/report\/?$/.exec(path) : null
  if (partgReport && can(me, 'sap.view'))
    return <Suspense fallback={<Loading label="Opening the report…" />}><PartGReportPage sapPlotId={Number(partgReport[1])} /></Suspense>
  const pvReport = area === 'admin' ? /^\/admin\/hem\/pvfit\/(sap|hem)\/(\d+)\/report\/?$/.exec(path) : null
  if (pvReport && can(me, 'sap.view'))
    return <Suspense fallback={<Loading label="Opening the report…" />}><PvFitReportPage kind={pvReport[1] as 'sap' | 'hem'} id={Number(pvReport[2])} /></Suspense>
  const packReport = area === 'admin' ? /^\/admin\/hem\/pack\/(sap|hem)\/(\d+)\/?$/.exec(path) : null
  if (packReport && can(me, 'sap.view'))
    return <Suspense fallback={<Loading label="Opening the report…" />}><DwellingPackPage kind={packReport[1] as 'sap' | 'hem'} id={Number(packReport[2])} /></Suspense>
  const sapApp = area === 'admin' ? /^\/admin\/sap\/app(?:\/(\d+))?\/?$/.exec(path) : null
  if (sapApp && can(me, 'sap.view'))
    return <Suspense fallback={<Loading label="Opening Kaizen SAP…" />}>{sapApp[1] ? <SapAppPage siteId={Number(sapApp[1])} /> : <SapStartPage />}</Suspense>
  const psiApp = area === 'admin' ? /^\/admin\/psi\/app(?:\/([0-9a-f-]{36}))?\/?$/i.exec(path) : null
  if (psiApp && can(me, 'psi.use'))
    return <Suspense fallback={<Loading label="Opening Kaizen PSI…" />}>{psiApp[1] ? <PsiAppPage id={psiApp[1]} /> : <PsiStartPage />}</Suspense>
  return <AreaPage area={area} path={path} me={me} />
}

function AreaPage({ area, path, me }: { area: Area; path: string; me: Me }) {
  useEffect(() => { rememberArea(area) }, [area])
  let content = <Empty title="Page not found" icon="info">That page doesn’t exist (yet).</Empty>
  for (const r of [...routesFor(area), ...(area === 'portal' ? PORTAL_EXTRA_ROUTES : area === 'admin' ? ARCHIVE_ROUTES : [])]) {
    const params = matchPath(r.path, path)
    if (!params) continue
    const Page = r.component
    content = r.perm && !can(me, r.perm) ? <Empty title="No access" icon="lock">You don’t have permission to see this page.</Empty> : <Page params={params} />
    break
  }
  return (
    <Shell area={area}>
      <Suspense fallback={<Loading />}>{content}</Suspense>
    </Shell>
  )
}

function NoAccess() {
  const { signOut } = useAuth()
  return (
    <AuthLayout title="No access yet">
      <h2>No access yet</h2>
      <p className="lead">Your account exists, but it isn’t linked to any part of Briary Hub yet. Please contact the Briary office and we’ll sort it out.</p>
      <a href="#" onClick={(e) => { e.preventDefault(); void signOut() }}>Sign out</a>
    </AuthLayout>
  )
}

function NotFoundPage() {
  return (
    <AuthLayout title="Not found">
      <h2>Page not found</h2>
      <p className="lead">That address doesn’t exist in Briary Hub.</p>
      <a href="/">Go to my home page</a>
    </AuthLayout>
  )
}
